import React, { useState, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Popover,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";

export const COUNTRY_OPTIONS = [
  { label: "Belgium", value: "belgium" },
  { label: "France", value: "france" },
  { label: "Luxembourg", value: "luxembourg" },
];

export const INDUSTRY_OPTIONS = [
  { label: "Architecture", value: "architecture" },
  { label: "Engineering", value: "engineering" },
  { label: "Construction", value: "construction" },
  { label: "Administration", value: "administration" },
  { label: "Industry", value: "industry" },
];

export const TEAM_SIZE_OPTIONS = [
  { label: "1 - 5", value: "one_to_five" },
  { label: "6 - 10", value: "six_to_ten" },
  { label: "11 - 20", value: "eleven_to_twenty" },
  { label: "21 - 50", value: "twenty_one_to_fifty" },
  { label: "51 - 100", value: "fifty_one_to_hundred" },
  { label: "101 - 200", value: "hundred_one_to_two_hundred" },
  { label: "200+", value: "two_hundred_plus" },
];

export const TEAM_SIZE_LABELS = TEAM_SIZE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

export const CITY_OPTIONS = [
  { label: "Brussels (BE)", value: "Brussels" },
  { label: "Antwerp (BE)", value: "Antwerp" },
  { label: "Ghent (BE)", value: "Ghent" },
  { label: "Charleroi (BE)", value: "Charleroi" },
  { label: "Liège (BE)", value: "Liège" },
  { label: "Paris (FR)", value: "Paris" },
  { label: "Roubaix (FR)", value: "Roubaix" },
];

const chipSx = (active) => ({
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 13,
  borderRadius: "10px",
  height: 34,
  px: 0.5,
  cursor: "pointer",
  transition: "all 0.15s ease",
  ...(active
    ? {
      backgroundColor: alpha("#079344", 0.12),
      color: "#079344",
      borderColor: "#079344",
      "&:hover": { backgroundColor: alpha("#079344", 0.18) },
    }
    : {
      backgroundColor: "rgba(255,255,255,0.9)",
      color: "#2A334E",
      borderColor: "rgba(42,51,78,0.2)",
      "&:hover": {
        backgroundColor: "#fff",
        borderColor: "rgba(42,51,78,0.35)",
      },
    }),
});

const FilterChip = ({ def, selected, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const count = selected.length;

  const handleToggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  return (
    <>
      <Chip
        variant="outlined"
        label={count > 0 ? `${def.label} (${count})` : def.label}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={chipSx(count > 0)}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              minWidth: 220,
              maxHeight: 360,
              overflow: "auto",
            },
          },
        }}
      >
        <Box sx={{ py: 0.5 }}>
          {def.options.map((opt) => (
            <FormControlLabel
              key={opt.value}
              sx={{ display: "flex", mx: 0, px: 2, py: 0.25 }}
              control={
                <Checkbox
                  size="small"
                  checked={selected.includes(opt.value)}
                  onChange={() => handleToggle(opt.value)}
                  sx={{
                    color: "rgba(42,51,78,0.3)",
                    "&.Mui-checked": { color: "#079344" },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: 13, color: "#2A334E", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  {opt.label}
                </Typography>
              }
            />
          ))}
        </Box>
      </Popover>
    </>
  );
};

const CompanyTableFilters = ({ filters, onFiltersChange }) => {
  const intl = useIntl();

  const filterDefs = useMemo(
    () => [
      {
        id: "countries",
        label: intl.formatMessage({ id: "internal.filters.country", defaultMessage: "Country" }),
        options: COUNTRY_OPTIONS,
      },
      {
        id: "industries",
        label: intl.formatMessage({ id: "internal.filters.industry", defaultMessage: "Industry" }),
        options: INDUSTRY_OPTIONS,
      },
      {
        id: "team_sizes",
        label: intl.formatMessage({ id: "internal.filters.team_size", defaultMessage: "Team size" }),
        options: TEAM_SIZE_OPTIONS,
      },
      {
        id: "cities",
        label: intl.formatMessage({ id: "internal.filters.city", defaultMessage: "City" }),
        options: CITY_OPTIONS,
      },
    ],
    [intl],
  );

  const totalActive = useMemo(
    () => Object.values(filters).reduce((sum, arr) => sum + (arr?.length || 0), 0),
    [filters],
  );

  const handleChange = useCallback(
    (filterId) => (newValues) => {
      onFiltersChange({ ...filters, [filterId]: newValues });
    },
    [filters, onFiltersChange],
  );

  const handleClearAll = useCallback(() => {
    const cleared = Object.keys(filters).reduce((acc, key) => ({ ...acc, [key]: [] }), {});
    onFiltersChange(cleared);
  }, [filters, onFiltersChange]);

  return (
    <Box
      sx={{
        px: 3,
        py: 1,
        backgroundColor: "#F4F5FA",
        borderBottom: "1px solid rgba(42,51,78,0.08)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <FilterListIcon sx={{ fontSize: 18, color: "#2A334E", opacity: 0.5, mr: 0.5 }} />

        {filterDefs.map((def) => (
          <FilterChip
            key={def.id}
            def={def}
            selected={filters[def.id] || []}
            onChange={handleChange(def.id)}
          />
        ))}

        {totalActive > 0 && (
          <Chip
            size="small"
            label="Clear all"
            deleteIcon={<CloseIcon sx={{ fontSize: "16px !important" }} />}
            onDelete={handleClearAll}
            onClick={handleClearAll}
            sx={{
              ml: 0.5,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 12,
              color: "#D32F2F",
              borderColor: "#D32F2F",
              height: 28,
              borderRadius: "8px",
              "& .MuiChip-deleteIcon": { color: "#D32F2F" },
            }}
            variant="outlined"
          />
        )}
      </Stack>
    </Box>
  );
};

export const EMPTY_COMPANY_FILTERS = {
  countries: [],
  industries: [],
  team_sizes: [],
  cities: [],
};

export default CompanyTableFilters;
